import React, { useState } from 'react';
import { faHotel, faPlane } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, formatPrice, formatPriceCurrency, getCurrency, getLogoUrl } from 'utils/utils';
import { faFileLines, faMoneyBill1 } from '@fortawesome/free-regular-svg-icons';
import Rating from 'components/ui/Rating/Rating';
import { DealModel } from 'ts/package/interfaces/PackageOfflineResponse';
import { dateFormat } from 'utils/shared/utils';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import GeneralConditionsModal from 'components/shared/GeneralConditionsModal';
import HandleFee from 'components/shared/HandleFee';
import SalesPrice from 'components/shared/SalesPrice';

import ReactCountryFlag from 'react-country-flag';
import { getBoardName } from 'utils/package';
import { blobUrl } from 'constants/generic';

interface BookSummaryProps {
  successPage?: boolean;
  deal: DealModel;
  couponAmount: string;
  setCouponAmount: (value: string) => void;
}

const BookSummary: FC<BookSummaryProps> = ({ deal, successPage, ...props }) => {
  const [t] = useTranslation();
  const [isConditionsOpen, setIsConditionsOpen] = useState(false);
  return (
    <div>
      <div
        className={
          'details-summary book-summary default-shadow flight-details-summary' +
          (successPage ? ' flight-details-summary-success' : '')
        }
      >
        <div className="title-underline text-lg mb-20">{t('passenger.order_details')}</div>

        {!!deal.package.flightOutBound && !!deal.package.flightInBound && (
          <Fragment>
            <div className="title text-st text-contrast mb-0">
              <FontAwesomeIcon icon={faPlane} className="mr-5" size="sm" />
              {t('flight.details')}
            </div>
            <div className="summary">
              {!!deal.package.flightOutBound && (
                <span className="summary-img">
                  <img
                    src={getLogoUrl(blobUrl, deal.package.flightOutBound.airline)}
                    alt={deal.package.flightOutBound.airline}
                  />
                  <span className="text-light">{deal.package.flightOutBound.airline}</span>
                </span>
              )}

              <div className="summary-road">
                <div className="summary-road-item">
                  <div>
                    <ReactCountryFlag
                      className="mr-5"
                      svg
                      countryCode={deal.departureAirport.countryCode}
                    />
                    <span>
                      {deal.departureAirport.desc} ({deal.departureAirport.code})
                    </span>
                    <br />
                    <span className="text-light text-xxs">{deal.departureAirport.desc}</span>
                  </div>

                  <div>
                    <span>
                      {dateFormat(
                        `${deal.package.flightOutBound.date}T${deal.package.flightOutBound.depTime}`,
                        'EEE - dd MMMM, yyyy'
                      )}{' '}
                    </span>
                    <span className="text-bold">
                      {dateFormat(
                        `${deal.package.flightOutBound.date}T${deal.package.flightOutBound.depTime}`,
                        'HH:mm'
                      )}
                    </span>
                  </div>
                </div>
                <div className="summary-road-item">
                  <div>
                    <ReactCountryFlag
                      className="mr-5"
                      svg
                      countryCode={deal.outboundArrivalCity.countryCode}
                    />
                    <span>
                      <span className="text-cap">
                        {deal.outboundArrivalCity.cityDesc.toLowerCase()}
                      </span>
                      ({deal.outboundArrivalCity.cityCode})
                    </span>
                    <br />
                    <span className="text-light text-xxs text-cap">
                      {deal.outboundArrivalCity.cityDesc.toLowerCase()}
                    </span>
                  </div>
                  <div>
                    <span>
                      {dateFormat(
                        `${deal.package.flightOutBound.date}T${deal.package.flightInBound.arrTime}`,
                        'EEE - dd MMMM, yyyy'
                      )}{' '}
                    </span>
                    <span className="text-bold">
                      {dateFormat(
                        `${deal.package.flightOutBound.date}T${deal.package.flightOutBound.arrTime}`,
                        'HH:mm'
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="summary">
              <span className="summary-img">
                <img
                  src={getLogoUrl(blobUrl, deal.package.flightInBound.airline)}
                  alt={deal.package.flightInBound.airline}
                />
                <span className="text-light">{deal.package.flightInBound.airline}</span>
              </span>

              <div className="summary-road">
                <div className="summary-road-item">
                  <div>
                    <ReactCountryFlag
                      className="mr-5"
                      svg
                      countryCode={deal.inboundDepartureCity.countryCode}
                    />
                    <span>
                      <span className="text-cap">
                        {deal.inboundDepartureCity.cityDesc.toLowerCase()}
                      </span>
                      ({deal.inboundDepartureCity.cityCode})
                    </span>
                    <br />
                    <span className="text-light text-xxs">
                      {deal.inboundDepartureCity.cityDesc}
                    </span>
                  </div>
                  {!!deal.package.flightInBound && (
                    <div>
                      <span>
                        {dateFormat(
                          `${deal.package.flightInBound.date}T${deal.package.flightInBound.depTime}`,
                          'EEE - dd MMMM, yyyy'
                        )}{' '}
                      </span>
                      <span className="text-bold">
                        {dateFormat(
                          `${deal.package.flightInBound.date}T${deal.package.flightInBound.depTime}`,
                          'HH:mm'
                        )}
                      </span>
                    </div>
                  )}
                </div>
                <div className="summary-road-item">
                  <div>
                    <ReactCountryFlag
                      className="mr-5"
                      svg
                      countryCode={deal.departureAirport.countryCode}
                    />
                    <span className="text-cap">
                      {deal.departureAirport.airCityLang.desc.toLowerCase()}
                    </span>
                    ({deal.departureAirport.code}
                    )
                    <br />
                    <span className="text-light text-xxs">{deal.departureAirport.desc}</span>
                  </div>
                  <div>
                    <span>
                      {dateFormat(
                        `${deal.package.flightInBound.date}T${deal.package.flightInBound.arrTime}`,
                        'EEE - dd MMMM, yyyy'
                      )}{' '}
                    </span>
                    <span className="text-bold">
                      {dateFormat(
                        `${deal.package.flightInBound.date}T${deal.package.flightInBound.arrTime}`,
                        'HH:mm'
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Fragment>
        )}

        <div className="summary-road">
          <div className="summary-road-title">
            <div className="title text-st text-contrast mb-0 text-lh">
              <FontAwesomeIcon icon={faHotel} className="mr-5" size="sm" />
              <span className="text-cap">{deal.package.hoteltDesc?.toLowerCase()}</span>
              <Rating value={deal.package.star} />
            </div>
          </div>
          <div className="summary-road-image">
            {!!deal.package.images?.getImages.length && (
              <img
                src={deal.package.images.getImages[0].imageLink}
                alt={deal.package.images.getImages[0].imageAlt}
              />
            )}
          </div>
        </div>
        <div className="summary-road text-sm text-lh">
          <div className="summary-road-item">
            <div>חבילה</div>
            <div className="text-bold mb-10">{deal.package.nameLang?.[0]?.desc}</div>
            <div className="text-sm">{t('search.check_in')}</div>
            <div className="text-bold mb-10">
              {dateFormat(deal.package.startDate, 'EEE - dd MMMM, yyyy')}
            </div>
            <div className="text-bold mb-10"></div>
            <div>{t('package.nights')}</div>
            <div className="text-bold mb-10">{deal.package.numberOfNights}</div>
          </div>

          <div className="summary-road-item">
            <div>{t('search.check_out')}</div>
            <div className="text-bold mb-10">
              {dateFormat(
                addDays(deal.package.numberOfNights, new Date(deal.package.startDate)),
                'EEE - dd MMMM, yyyy'
              )}
            </div>
            <div>{t('passenger.room_count')}</div>
            <div className="text-bold mb-10">1</div>
          </div>
        </div>

        {!successPage && (
          <Fragment>
            <div className="divider mb-20"></div>
            <div className="title text-st text-contrast">
              <FontAwesomeIcon icon={faFileLines as any} className="mr-5" size="sm" />
              {t('package.important_info')}
            </div>
            <div className="text-sm mb-20">
              <span>{t('package.it_is_your_res')}</span>
              <span
                className="text-contrast text-bold cur-poi"
                onClick={() => setIsConditionsOpen(true)}
              >
                {t('package.terms_of_use')}
              </span>
              <span>{t('package.and_all_conditions')}</span>
              <HandleFee name="OfflinePackages" />
            </div>
          </Fragment>
        )}

        <div className="divider mb-20"></div>
        <div className="flex-between mb-5 title text-st text-contrast">
          <div className="">
            <FontAwesomeIcon icon={faMoneyBill1 as any} className="mr-5" size="sm" />
            {t('flight.booking_summary')}
          </div>
          <div>{formatPrice(deal.total, deal.currency)}</div>
        </div>
        {!!deal.room && !!deal.room.name && (
          <div className="flex-between mb-15 text-sm">
            <div className="">
              <span>{deal.room?.name.name} </span>
              <span className="text-light text-xsm">({getBoardName(deal.room?.board.name)})</span>
            </div>

            <div>{formatPrice(deal.totalPrice, deal.currency)}</div>
          </div>
        )}

        <div className="divider"></div>

        <div className="mt-20 flex-between text-lg text-bold">
          <div>{t('flight.total_per_package')}</div>
          {!successPage && (
            <div className="text-contrast">{formatPrice(deal.totalPrice, deal.currency)}</div>
          )}

          {successPage && (
            <div className="text-contrast">
              {formatPriceCurrency(deal.totalPrice, '₪')}
              {deal.couponAmount && (
                <span className="text-sm">
                  {' '}
                  (+ {formatPriceCurrency(deal.couponAmount, getCurrency(deal.currency))})
                </span>
              )}
            </div>
          )}
        </div>
        {isConditionsOpen && <GeneralConditionsModal onClose={() => setIsConditionsOpen(false)} />}
      </div>

        <SalesPrice
          currency={deal.currency}
          setSaleValue={props.setCouponAmount}
          saleValue={props.couponAmount}
          price={deal.totalPrice}
          onClickApply={() => {}}
        />
      
    </div>
  );
};

export default withErrorBoundary(BookSummary, 'BookSummary');
